import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleDot, LuKeyRound, LuLoaderCircle, LuMail, LuMapPin, LuSend, LuShieldCheck, LuSparkles, LuUserCog, LuUserPen } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { Field, TextArea, TextInput } from '../../../components/form/FormControls';
import useAccessText from '../accessText';
import { getRoles, isActiveEmployee } from '../accessUtils';
import { PassiveTag, PersonAvatar, RoleChip } from './accessBits';

const ROW = 'tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3 tw-py-2.5 tw-text-left dark:tw-border-white/10 dark:tw-bg-white/[0.03]';

const KINDS = {
  admins: { icon: LuShieldCheck, gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30', role: 'admin' },
  restricted: { icon: LuUserCog, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', role: 'Kısıtlı Admin' },
  online: { icon: LuCircleDot, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
};

const NameBlock = ({ employee, children }) => (
  <span className="tw-min-w-0 tw-flex-1">
    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
      <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{employee.eName} {employee.eSurname}</span>
      {!isActiveEmployee(employee) && <PassiveTag />}
    </span>
    <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{employee.eMail || '—'}</span>
    {children}
  </span>
);

NameBlock.propTypes = { employee: PropTypes.object.isRequired, children: PropTypes.node };

/**
 * Admins, restricted admins or online users (the old three windows). A
 * person opens their permissions; online users can also get a message and
 * show the page they are on.
 */
export const PeopleDialog = ({ kind, people, loading, onlineActivity, onClose, onSelect, onMessage }) => {
  const { label, language, placeLabel, roleLabel } = useAccessText();
  const meta = KINDS[kind] || KINDS.admins;
  const Icon = meta.icon;
  return (
    <Modal
      isOpen={Boolean(kind)}
      onClose={onClose}
      size="lg"
      title={kind ? label(`people.${kind}.title`) : ''}
      subtitle={label('people.count', { count: people.length })}
      closeLabel={label('people.close')}
      lang={language}
      icon={(
        <GlassIcon gradient={meta.gradient} size="xs">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
    >
      {loading && <p className="tw-py-8 tw-text-center tw-text-sm tw-text-slate-500">{label('people.loading')}</p>}
      {!loading && people.length === 0 && kind && (
        <p className="tw-py-8 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label(`people.${kind}.empty`)}</p>
      )}
      {!loading && people.length > 0 && (
        <ul className="tw-space-y-2">
          {people.map((emp) => {
            if (kind !== 'online') {
              return (
                <li key={emp.id}>
                  <button type="button" onClick={() => onSelect(emp)} title={label('people.editHint')} className={`${ROW} tw-transition-colors hover:tw-border-teal-400/50 hover:tw-bg-teal-50/60 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-teal-500/[0.06]`}>
                    <PersonAvatar employee={emp} passive={!isActiveEmployee(emp)} />
                    <NameBlock employee={emp} />
                    <RoleChip>{meta.role}</RoleChip>
                  </button>
                </li>
              );
            }
            const roles = getRoles(emp);
            const roleText = roles.includes('admin') ? 'admin' : roles.includes('Kısıtlı Admin') ? 'Kısıtlı Admin' : roleLabel('employee');
            const activity = onlineActivity[emp.id] || {};
            const place = placeLabel(activity.page);
            return (
              <li key={emp.id} className={`${ROW} tw-flex-wrap sm:tw-flex-nowrap`}>
                <PersonAvatar employee={emp} online passive={!isActiveEmployee(emp)} />
                <NameBlock employee={emp}>
                  <span className="tw-mt-1 tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-xs">
                    <LuMapPin aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-emerald-600 dark:tw-text-emerald-400" />
                    {place ? (
                      <>
                        <span className="tw-shrink-0 tw-text-slate-400 dark:tw-text-slate-500">{place.group} ·</span>
                        <span className="tw-truncate tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">{place.title}</span>
                        {activity.duration && <span className="tw-shrink-0 tw-rounded-full tw-bg-emerald-500/10 tw-px-1.5 tw-text-[10px] tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">{activity.duration}</span>}
                      </>
                    ) : (
                      <span className="tw-italic tw-text-slate-400">{label('people.noPage')}</span>
                    )}
                  </span>
                </NameBlock>
                <span className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-1.5 sm:tw-w-auto">
                  <RoleChip>{roleText}</RoleChip>
                  <Button size="sm" onClick={() => onMessage(emp)} title={label('people.messageHint')}>
                    <LuSend aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-sky-600 dark:tw-text-sky-300" />
                    {label('people.message')}
                  </Button>
                  <Button size="sm" onClick={() => onSelect(emp)} title={label('people.editHint')}>
                    <LuUserPen aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />
                    {label('people.access')}
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
};

PeopleDialog.propTypes = {
  kind: PropTypes.oneOf(['admins', 'restricted', 'online']),
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onlineActivity: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onMessage: PropTypes.func.isRequired,
};

/** A message to an online user (written to the same `messages` the Messenger uses). */
export const MessageDialog = ({ target, text, sending, onText, onSend, onClose }) => {
  const { label, language } = useAccessText();
  return (
    <Modal
      isOpen={Boolean(target)}
      onClose={onClose}
      size="md"
      dismissible={!sending}
      title={label('message.title')}
      closeLabel={label('message.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
          <LuSend aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={sending}>{label('message.cancel')}</Button>
          <Button variant="primary" onClick={onSend} disabled={sending || !text.trim()}>
            {sending ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {sending ? label('message.sending') : label('message.send')}
          </Button>
        </>
      )}
    >
      {target && (
        <div className="tw-space-y-4">
          <div className={ROW}>
            <PersonAvatar employee={target} />
            <span className="tw-min-w-0 tw-flex-1">
              <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{target.eName} {target.eSurname}</span>
              <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{target.eMail || label('message.noEmail')}</span>
            </span>
          </div>
          <Field label={label('message.label')} htmlFor="page-access-message" hint={label('message.hint')}>
            <TextArea
              id="page-access-message"
              rows={5}
              autoFocus
              placeholder={label('message.placeholder')}
              value={text}
              onChange={(event) => onText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) onSend();
              }}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
};

MessageDialog.propTypes = {
  target: PropTypes.object,
  text: PropTypes.string.isRequired,
  sending: PropTypes.bool,
  onText: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/** Sets a new password for the selected employee (Cloud Function), optionally e-mailing it. */
export const PasswordDialog = ({ isOpen, employee, password, sendEmail, saving, error, onPassword, onGenerate, onSendEmail, onSubmit, onClose }) => {
  const { label, language } = useAccessText();
  return (
    <Modal
      isOpen={isOpen && Boolean(employee)}
      onClose={onClose}
      size="md"
      dismissible={!saving}
      title={label('password.title')}
      subtitle={employee ? `${employee.eName || ''} ${employee.eSurname || ''}`.trim() : undefined}
      closeLabel={label('password.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuKeyRound aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('password.cancel')}</Button>
          <Button variant="primary" onClick={onSubmit} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuKeyRound aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('password.saving') : label('password.submit')}
          </Button>
        </>
      )}
    >
      {employee && (
        <div className="tw-space-y-4">
          <div className={ROW}>
            <PersonAvatar employee={employee} passive={!isActiveEmployee(employee)} />
            <NameBlock employee={employee} />
          </div>
          <Field label={label('password.label')} htmlFor="page-access-password" error={error || undefined}>
            <div className="tw-flex tw-gap-2">
              <TextInput
                id="page-access-password"
                type="text"
                value={password}
                onChange={(event) => onPassword(event.target.value)}
                placeholder={label('password.placeholder')}
                autoComplete="new-password"
                error={error || undefined}
                className="tw-flex-1 tw-font-mono"
              />
              <Button onClick={onGenerate} title={label('password.generateHint')}>
                <LuSparkles aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-600 dark:tw-text-violet-300" />
                {label('password.generate')}
              </Button>
            </div>
          </Field>
          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3.5 tw-py-3 tw-text-sm tw-font-medium tw-text-slate-700 dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-text-slate-200">
            <input type="checkbox" checked={sendEmail} onChange={(event) => onSendEmail(event.target.checked)} className="tw-h-4 tw-w-4 tw-accent-teal-600" />
            <LuMail aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
            {label('password.sendEmail')}
          </label>
        </div>
      )}
    </Modal>
  );
};

PasswordDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  employee: PropTypes.object,
  password: PropTypes.string.isRequired,
  sendEmail: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onPassword: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onSendEmail: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
